import { useEffect, useRef, useState } from 'react';
import { googleSignIn } from '../api/googleSignIn';
import { nativeGoogle } from '../lib/nativeGoogle';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** Said under the phone's button when Google's sheet ended without a token and the person did not close it. */
export const GOOGLE_FAILED_ON_PHONE = 'Google could not sign you in on this phone. Sign in with your email and password, or try again later.';

/** Google Identity Services, loaded once, on the website only. */
export function loadGoogleIdentity() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window.'));
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);

  window.__ingoGsi ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => (window.google?.accounts?.id ? resolve(window.google.accounts.id) : reject(new Error('Google did not load.')));
    script.onerror = () => {
      window.__ingoGsi = undefined;
      reject(new Error('Google could not be reached.'));
    };
    document.head.appendChild(script);
  });

  return window.__ingoGsi;
}

/** The phone app (Capacitor), where Google's web sign-in does not work inside the app's WebView. */
function onPhone() {
  return Boolean(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.());
}

/** Google's "G", in its own colours (Google's sign-in branding). */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const PHONE_BUTTON = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.625rem',
  width: '100%',
  maxWidth: 320,
  height: 46,
  padding: '0 1.25rem',
  border: '1px solid #747775',
  borderRadius: 9999,
  background: '#fff',
  color: '#1f1f1f',
  font: 'inherit',
  fontSize: '0.9375rem',
  fontWeight: 600,
  cursor: 'pointer',
};

/**
 * "Continue with Google" for a client or a rider (InGo ADR 0023), shown only
 * while Settings offers Google with a web client id.
 *
 * - **Website** (ingo-e0, 2 Oct 2026): Google's own button, Google Identity Services.
 * - **Phone** (ingo-f1, 5 Oct 2026; Rio: "people are not going to use the webapp
 *   we are using the android and ios apps"): Google refuses its web sign-in in an
 *   app's WebView, so the button is the app's, in Google's colours, and opens
 *   Google's sheet through Credential Manager (src/lib/nativeGoogle.js). It is
 *   drawn only where that sheet can open: a build with the plugin, and Google
 *   Play services on the phone.
 *
 * Both hand the token to the same door, `POST {realm}/auth/google`.
 * `onResult(result, idToken)` gets the API's answer (`signed_in` or, for a rider
 * with no account, `sign_up`); `onError(error, idToken)` a refusal.
 */
export default function GoogleSignInButton({ realm, onResult, onError, google = googleSignIn, loadIdentity = loadGoogleIdentity, phone = onPhone, native = nativeGoogle }) {
  const slot = useRef(null);
  const handlers = useRef({ onResult, onError });
  handlers.current = { onResult, onError };
  // `{ clientId, onPhone }` once Settings offers Google and this device can use it; null otherwise.
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const onPhoneNow = phone();
    google.config().then(
      async (config) => {
        const clientId = config?.clientId ?? null;
        const usable = clientId !== null && (!onPhoneNow || (await native.available()));
        if (!cancelled) setOffer(usable ? { clientId, onPhone: onPhoneNow } : null);
      },
      () => !cancelled && setOffer(null),
    );

    return () => {
      cancelled = true;
    };
  }, [google, native, phone]);

  useEffect(() => {
    if (!offer || offer.onPhone) return undefined;
    let cancelled = false;
    loadIdentity().then(
      (gsi) => {
        if (cancelled || !slot.current) return;
        gsi.initialize({
          client_id: offer.clientId,
          callback: async ({ credential }) => {
            try {
              handlers.current.onResult(await google.signIn(realm, credential), credential);
            } catch (error) {
              handlers.current.onError(error, credential);
            }
          },
        });
        gsi.renderButton(slot.current, { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', shape: 'pill', width: 300 });
      },
      () => {},
    );

    return () => {
      cancelled = true;
    };
  }, [offer, google, loadIdentity, realm]);

  if (!offer) return null;

  if (!offer.onPhone) {
    return <div ref={slot} data-testid="google-sign-in" style={{ display: 'flex', justifyContent: 'center', margin: '0.75rem 0' }} />;
  }

  // One sheet at a time: the button is disabled while it is open, so a second press asks Google nothing.
  const press = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await native.token(offer.clientId);
      if (answer.kind === 'token') {
        try {
          handlers.current.onResult(await google.signIn(realm, answer.idToken), answer.idToken);
        } catch (error) {
          handlers.current.onError(error, answer.idToken);
        }
      } else if (answer.kind !== 'cancelled') {
        setProblem(GOOGLE_FAILED_ON_PHONE);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="google-sign-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0.75rem 0' }}>
      <button type="button" onClick={press} disabled={busy} style={{ ...PHONE_BUTTON, opacity: busy ? 0.6 : 1 }}>
        <GoogleMark />
        <span>{busy ? 'Opening Google…' : 'Continue with Google'}</span>
      </button>
      {problem ? (
        <p role="alert" className="auth-message auth-message--error" style={{ textAlign: 'center' }}>
          {problem}
        </p>
      ) : null}
    </div>
  );
}
